import { ResponsiveBar } from '@nivo/bar';
import {
  OrderSetJobsStatsRow,
  OrderSetJobsStatsRows,
  OrderSetJobsTotalsRow,
} from '@warebee/shared/dataset-queries';
import _ from 'lodash';
import React, { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { t } from 'i18next';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { TwTheme } from '../../../Tw';
import {
  formatInteger,
  FormattedValueWithUnit,
} from '../../common/formatHelper';
import useFormatter from '../../common/useFormatter';
import ButtonSwitchMulti, {
  ButtonSwitchMultiOption,
} from '../../components/actions/ButtonSwitchMulti';
import DropdownSelector from '../../components/actions/DropdownSelector';
import { ToggleDatasetTableLink } from '../../components/importer/DatasetActionHelpers';
import LoadingIndicator from '../../components/LoadingIndicator';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';
import { StatListItem } from '../../components/stats/StatListItem';
import {
  analyzeOrderJobsStatsByPicklistSetId,
  analyzeOrderJobsTotalsByPicklistSetId,
  analyzeResultById,
} from '../store/analyze.state';
import { pickingPolicy } from '../store/pickingPolicy/pickingPolicy.state';
import { SimulationAnalyzedOrdersPanelProps } from './SimulationAnalyzedOrdersPanel';

export type SimulationAnalyzedPicklistsPanelBaseProps =
  SimulationAnalyzedOrdersPanelProps & {
    isLoading: boolean;
    orderJobsStats: OrderSetJobsStatsRows;
    generatedJobsTotals: OrderSetJobsTotalsRow;
    analyzedJobsTotals: OrderSetJobsTotalsRow;
  };

type OrderBatchKPIs = Pick<
  OrderSetJobsStatsRow,
  'picklistCount' | 'totalNetWeight' | 'totalVolume'
>;
type OrderBatchKPI = keyof OrderBatchKPIs;

const SimulationAnalyzedPicklistsPanelBase: React.FC<
  SimulationAnalyzedPicklistsPanelBaseProps
> = props => {
  const { t } = useTranslation('simulation');
  const formatter = useFormatter();
  const policy = useRecoilValue(pickingPolicy);
  const colors = TwTheme.extend.colors;

  const [selectedRuleId, setSelectedRuleId] = useState<string>(null);
  const [selectedKPI, setSelectedKPI] =
    useState<OrderBatchKPI>('picklistCount');
  const [showOrderStatsList, setShowOrderStatsList] = useState(false);

  const ruleIds = _(props.orderJobsStats)
    .map(r => r.orderLineRuleId)
    .uniq()
    .value();

  const ruleTitles = _.reduce(
    policy.orderLineRules,
    (acc, rule) => ({ ...acc, [rule.id]: rule.title }),
    {},
  );
  const jobsCountByAffectedOrders: Record<any, OrderBatchKPIs> = _(
    props.orderJobsStats,
  )
    .filter(
      r => _.isNil(selectedRuleId) || r.orderLineRuleId === selectedRuleId,
    )
    .reduce(
      (acc, row) => ({
        ...acc,
        [row.affectedOrdersCount]: {
          picklistCount:
            (acc[row.affectedOrdersCount]?.picklistCount ?? 0) +
            row.picklistCount,
          totalNetWeight:
            (acc[row.affectedOrdersCount]?.totalNetWeight ?? 0) +
            row.totalNetWeight,
          totalVolume:
            (acc[row.affectedOrdersCount]?.totalVolume ?? 0) + row.totalVolume,
        },
      }),
      {},
    );

  const totalAffectedPicklistsCount = _.sumBy(
    _.values(jobsCountByAffectedOrders),
    v => v.picklistCount,
  );

  const hasWeightData = _.some(
    _.values(jobsCountByAffectedOrders),
    v => v.totalNetWeight > 0,
  );
  const hasVolumeData = _.some(
    _.values(jobsCountByAffectedOrders),
    v => v.totalVolume > 0,
  );

  const kpis: (ButtonSwitchMultiOption & { key: OrderBatchKPI })[] = [
    { key: 'picklistCount', label: t`Tasks` },
    ...(hasWeightData
      ? [{ key: 'totalNetWeight' as OrderBatchKPI, label: t`Avg. Weight` }]
      : []),
    ...(hasVolumeData
      ? [{ key: 'totalVolume' as OrderBatchKPI, label: t`Avg. Cube` }]
      : []),
  ];

  function getStatsValue(stats: OrderBatchKPIs): FormattedValueWithUnit {
    switch (selectedKPI) {
      case 'picklistCount':
        return {
          value: formatInteger(stats.picklistCount),
          fullString: formatInteger(stats.picklistCount) + t`picklists`,
          unit: t`tasks`,
          raw: stats.picklistCount,
          unitCode: 'picklists',
        };
      case 'totalNetWeight':
        return formatter.formatWeight(
          stats.totalNetWeight / stats.picklistCount,
        );
      case 'totalVolume':
        return formatter.formatVolume(stats.totalVolume / stats.picklistCount);
    }
  }

  const hasJobsDiff =
    props.generatedJobsTotals?.totalLines !==
    props.analyzedJobsTotals?.totalLines;

  const hasLocationsDiff =
    props.generatedJobsTotals?.totalLocations !==
    props.analyzedJobsTotals?.totalLocations;

  return (
    <StatGroup
      title={t`Jobs`}
      subTitle={t`Stats`}
      helpNavTo={'simulation/analyse/simulation-analyse'}
      titleAction={
        <DropdownSelector
          className={'text-xs'}
          classNameLabel="flex-1"
          headerMode
          DropAlignRight
          // label={t('Rule')}
          value={selectedRuleId}
          values={[null, ...ruleIds]}
          renderValue={v => ruleTitles[v] ?? t`All`}
          onChange={v => setSelectedRuleId(v)}
        />
      }
    >
      <Stat
        hasHelper
        isPreview
        title={t`Lines`}
        isFullWidth
        inPanelMode
        hasListDecimals
        isLoading={props.isLoading}
        isComparable={hasJobsDiff}
        value={
          formatter.formatInteger(props.generatedJobsTotals?.totalLines).value
        }
        valueBefore={formatter.formatInteger(
          props.generatedJobsTotals?.totalLines,
        )}
        valueAfter={formatter.formatInteger(
          props.analyzedJobsTotals?.totalLines,
        )}
      />

      <Stat
        hasHelper
        isPreview
        title={t`Visits`}
        isFullWidth
        inPanelMode
        hasListDecimals
        isLoading={props.isLoading}
        isComparable={hasLocationsDiff}
        value={
          hasLocationsDiff
            ? null
            : formatter.formatInteger(props.generatedJobsTotals?.totalLocations)
                .value
        }
        valueBefore={formatter.formatInteger(
          props.generatedJobsTotals?.totalLocations,
        )}
        valueAfter={formatter.formatInteger(
          props.analyzedJobsTotals?.totalLocations,
        )}
        valueDiffPercent={
          formatter.formatShare(
            Math.abs(
              (props.analyzedJobsTotals?.totalLocations -
                props.generatedJobsTotals?.totalLocations) /
                props.generatedJobsTotals?.totalLocations,
            ),
          ).value
        }
      />

      <Stat
        hasHelper
        isPreview
        title={t`Profile by`}
        isFullWidth
        inPanelMode
        isLoading={props.isLoading}
        value={formatInteger(totalAffectedPicklistsCount)}
        unitOfMeasure={t`tasks`}
        switchUnitOfMeasure={
          <ButtonSwitchMulti
            autoSize
            buttonType="minimal"
            options={kpis}
            selectedIndex={_.findIndex(kpis, v => v.key === selectedKPI)}
            onClick={index => {
              setSelectedKPI(kpis[index].key);
            }}
          />
        }
      >
        <div
          data-component="BatchProfileChartContainer"
          className="mt-4 h-40 w-full"
        >
          <ResponsiveBar
            data={_.map(
              jobsCountByAffectedOrders,
              (stats, affectedOrdersCount) => ({
                id: `${affectedOrdersCount}`,
                label: `${affectedOrdersCount} order(s)`,
                value:
                  selectedKPI === 'picklistCount'
                    ? stats.picklistCount
                    : selectedKPI === 'totalNetWeight'
                      ? stats.totalNetWeight / stats.picklistCount
                      : stats.totalVolume / stats.picklistCount,
              }),
            )}
            keys={['value']}
            indexBy="id"
            margin={{ top: 0, right: 0, bottom: 25, left: 35 }}
            padding={0.3}
            colors={[colors.picklistLines.full]}
            borderRadius={2}
            enableLabel={false}
            axisBottom={{
              tickSize: 0,
              tickPadding: 5,
              tickRotation: 0,
              format: value => `${value}`,
              tickValues: 10,
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 5,
              tickRotation: 0,
            }}
            markers={[
              {
                axis: 'y',
                value: _.meanBy(
                  Object.values(jobsCountByAffectedOrders),
                  stats =>
                    selectedKPI === 'picklistCount'
                      ? stats.picklistCount
                      : selectedKPI === 'totalNetWeight'
                        ? stats.totalNetWeight / stats.picklistCount
                        : stats.totalVolume / stats.picklistCount,
                ),
                lineStyle: {
                  stroke: 'hsl(60, 100%, 60%)',
                  strokeWidth: 1,
                  strokeDasharray: '2,2',
                },
              },
            ]}
            animate={true}
            enableGridX={false}
            enableGridY={true}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fontSize: 10,
                    fill: '#64748B',
                  },
                },
              },
              grid: {
                line: {
                  stroke: 'rgba(255, 255, 255, 0.2)',
                },
              },
              tooltip: {
                container: {
                  fontSize: '12px',
                },
              },
            }}
            tooltip={({ data }) => {
              const stats = jobsCountByAffectedOrders[data.id];
              const formatted = getStatsValue(stats);
              const average = _.meanBy(
                Object.values(jobsCountByAffectedOrders),
                stats =>
                  selectedKPI === 'picklistCount'
                    ? stats.picklistCount
                    : selectedKPI === 'totalNetWeight'
                      ? stats.totalNetWeight / stats.picklistCount
                      : stats.totalVolume / stats.picklistCount,
              );

              return (
                <div
                  data-component="BatchProfileTooltip"
                  className={classNames(
                    'z-dropdown_menu flex w-full flex-col',
                    'animate-appear',
                    'bg-app-panel-dark/90 backdrop-blur backdrop-filter',
                    'shadow-xl',
                    'p-2',
                    'rounded',
                  )}
                >
                  <div className="font-semibold">
                    {data.id}
                    <span className="ml-1 opacity-50">{t`order(s)`}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span>
                      {selectedKPI === 'picklistCount'
                        ? t('Tasks')
                        : selectedKPI === 'totalNetWeight'
                          ? t('Avg. Weight')
                          : t('Avg. Cube')}
                      :
                    </span>
                    <span className="font-medium">
                      {formatted.value}
                      {formatted.unit && (
                        <span className="ml-1 opacity-50">
                          {formatted.unit}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="mt-2 border-t border-white/10 pt-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-yellow-400">{t`Average`}:</span>
                      <span className="font-medium text-yellow-400">
                        {selectedKPI === 'picklistCount'
                          ? formatInteger(average)
                          : selectedKPI === 'totalNetWeight'
                            ? formatter.formatWeight(average).value
                            : formatter.formatVolume(average).value}
                        {formatted.unit && (
                          <span className="ml-1 opacity-50">
                            {formatted.unit}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span
              className={classNames(
                'text-menu-text/60 cursor-pointer text-sm hover:underline',
              )}
              onClick={() => {
                setShowOrderStatsList(!showOrderStatsList);
              }}
            >
              {showOrderStatsList ? t`Hide Order Stats` : t`Show Order Stats`}
            </span>

            <ToggleDatasetTableLink
              className="justify-self-end"
              dataType={'orders'}
              title={t`Jobs`}
              // datasetId={}
            />
          </div>

          {showOrderStatsList && (
            <div data-component="BatchProfileListDetails" className="mt-4">
              {_.map(
                jobsCountByAffectedOrders,
                (stats, affectedOrdersCount) => {
                  const formatted = getStatsValue(stats);
                  return (
                    <StatListItem
                      key={affectedOrdersCount}
                      title={t('{{count}} order', {
                        count: Number(affectedOrdersCount),
                      })}
                      value={formatted.value}
                      unitOfMeasure={formatted.unit}
                    />
                  );
                },
              )}
            </div>
          )}
        </div>
      </Stat>
    </StatGroup>
  );
};

export const SimulationAnalyzePicklistsPanel: React.FC<
  SimulationAnalyzedOrdersPanelProps
> = props => {
  const analyzeResult = useRecoilValue(analyzeResultById(props.analyzeId));
  const orderJobsStatsLoadable = useRecoilValueLoadable(
    analyzeOrderJobsStatsByPicklistSetId(analyzeResult.analyzedPicklistSet?.id),
  );
  const generatedJobsTotalsLoadable = useRecoilValueLoadable(
    analyzeOrderJobsTotalsByPicklistSetId(
      analyzeResult.generatedPicklistSet?.id,
    ),
  );
  const analyzedJobsTotalsLoadable = useRecoilValueLoadable(
    analyzeOrderJobsTotalsByPicklistSetId(
      analyzeResult.analyzedPicklistSet?.id,
    ),
  );

  const isLoading =
    orderJobsStatsLoadable.state === 'loading' ||
    generatedJobsTotalsLoadable.state === 'loading' ||
    analyzedJobsTotalsLoadable.state === 'loading';
  const isReady =
    orderJobsStatsLoadable.state === 'hasValue' ||
    generatedJobsTotalsLoadable.state === 'hasValue' ||
    analyzedJobsTotalsLoadable.state === 'hasValue';
  return (
    <Suspense fallback={<LoadingIndicator message={t`Loading Jobs Stats`} />}>
      <SimulationAnalyzedPicklistsPanelBase
        analyzeId={props.analyzeId}
        isLoading={isLoading}
        orderJobsStats={isReady ? orderJobsStatsLoadable.getValue() : null}
        generatedJobsTotals={
          isReady ? _.head(generatedJobsTotalsLoadable.getValue()) : null
        }
        analyzedJobsTotals={
          isReady ? _.head(analyzedJobsTotalsLoadable.getValue()) : null
        }
      />
    </Suspense>
  );
};
